// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react';
import {
    Box,
    Button,
    SpaceBetween,
    Container,
    Header,
    Alert,
    Link,
    TextContent,
} from '@cloudscape-design/components';
import { CodeView } from '@cloudscape-design/code-view';
import Breadcrumbs from '../../Utils/Breadcrumbs';
import { useHistory } from 'react-router-dom';
import { IMPORT } from '../../routes';

const OnboardingGuidePage = () => {
    const history = useHistory();

    const cdkInstallationCode = `# Step 1: Add Brazil dependency to your Config file
dependencies = {
    1.0 = {
        ResourceLensCDKConstructs = 1.0;
    };
};

# Step 2: Add npm package to your package.json
"dependencies": {
    "@amzn/resource-lens-constructs": "1.0.0"
}`;

    const cdkSingleRegionCode = `import { ResourceLensGlobalOnboarding, ResourceLensRegionalOnboarding } from '@amzn/resource-lens-constructs';
import { Stack, StackProps } from '@amzn/secure-cdk-sdo';
import { Construct } from 'constructs';

export class MyResourceLensStack extends Stack {
  constructor(scope: Construct, id: string, props?: StackProps) {
    super(scope, id, props);

    // Global resources (deploy once per account)
    new ResourceLensGlobalOnboarding(this, 'ResourceLensGlobal');

    // Regional resources (current region only)
    new ResourceLensRegionalOnboarding(this, 'ResourceLensRegional', {
      region: Stack.of(this).region,
    });
  }
}`;

    const cdkMultiRegionCode = `// Deploy this stack in us-east-1
export class ResourceLensUSEast1Stack extends Stack {
  constructor(scope: Construct, id: string, props?: StackProps) {
    super(scope, id, props);

    // Global resources (deploy only once per account)
    new ResourceLensGlobalOnboarding(this, 'ResourceLensGlobal');

    // Regional resources for us-east-1
    new ResourceLensRegionalOnboarding(this, 'ResourceLensRegional', {
      region: 'us-east-1',
    });
  }
}

// Deploy this stack in us-west-2
export class ResourceLensUSWest2Stack extends Stack {
  constructor(scope: Construct, id: string, props?: StackProps) {
    super(scope, id, props);

    // No global resources needed (already created in us-east-1 stack)
    // Regional resources for us-west-2
    new ResourceLensRegionalOnboarding(this, 'ResourceLensRegional', {
      region: 'us-west-2',
    });
  }
}`;

    const cdkDeploymentCode = `# Deploy your CDK stack using your pipeline`;

    // Fixed breadcrumbs - removed duplicate ResourceLens
    const breadcrumbItems = [
        { text: 'Onboarding Guide', href: '/onboarding-guide' },
    ];

    const mainHeader = (
        <Header
            variant="h1"
            description="Complete guide for onboarding AWS accounts to ResourceLens"
            actions={
                <Button
                    variant="primary"
                    onClick={() => history.push(IMPORT)}
                >
                    Go to Import Page
                </Button>
            }
        >
            ResourceLens Account Onboarding Guide
        </Header>
    );

    return (
        <SpaceBetween size="l">
            <Breadcrumbs items={breadcrumbItems} />
            <Container header={mainHeader}>
                <SpaceBetween size="l">
                    <QuickNavigationSection />
                    <IntroductionSection />
                    <PrerequisitesSection />
                    <CDKConstructsSection 
                        cdkInstallationCode={cdkInstallationCode}
                        cdkSingleRegionCode={cdkSingleRegionCode}
                        cdkMultiRegionCode={cdkMultiRegionCode}
                        cdkDeploymentCode={cdkDeploymentCode}
                    />
                    <CloudFormationSection history={history} />
                    <VerificationSection history={history} />
                </SpaceBetween>
            </Container>
        </SpaceBetween>
    );
};

// Quick Navigation Section Component
const QuickNavigationSection = () => (
    <Alert type="info" header="Quick Navigation">
        <TextContent>
            <ul>
                <li><strong>Introduction & Overview</strong> - What ResourceLens provides</li>
                <li><strong>Prerequisites</strong> - What you need before starting</li>
                <li><strong>Option 1: CDK Constructs</strong> - Recommended deployment method</li>
                <li><strong>Option 2: CloudFormation Templates</strong> - Alternative deployment method</li>
                <li><strong>Verification & Next Steps</strong> - How to confirm successful deployment</li>
            </ul>
        </TextContent>
    </Alert>
);

// Introduction & Overview Section Component
const IntroductionSection = () => (
    <Container
        header={
            <Header variant="h2">
                Introduction & Overview
            </Header>
        }
    >
        <SpaceBetween size="m">
            <TextContent>
                <p>
                    ResourceLens is a comprehensive AWS resource discovery and visualization tool that helps you:
                </p>
                <ul>
                    <li><strong>Discover resources</strong> across your AWS accounts and regions</li>
                    <li><strong>Visualize relationships</strong> between AWS resources</li>
                    <li><strong>Create diagrams</strong> for documentation and analysis</li>
                    <li><strong>Monitor resource changes</strong> over time</li>
                </ul>
                <p>
                    To enable ResourceLens discovery in your AWS accounts, you must first deploy the necessary 
                    IAM roles and AWS Config resources. This guide provides two methods for deployment.
                </p>
            </TextContent>

            <Alert type="warning" header="Important: Deploy First, Then Import">
                You must deploy ResourceLens resources to your AWS accounts <strong>before</strong> importing 
                them for discovery. The proper sequence is: Deploy → Import → Discover.
            </Alert>
        </SpaceBetween>
    </Container>
);

// Prerequisites Section Component
const PrerequisitesSection = () => (
    <Container
        header={
            <Header variant="h2">
                Prerequisites
            </Header>
        }
    >
        <SpaceBetween size="m">
            <TextContent>
                <h3>AWS Account Access</h3>
                <ul>
                    <li>Administrative access to target AWS accounts</li>
                    <li>Permissions to create IAM roles and policies</li>
                    <li>Permissions to configure AWS Config (if not already enabled)</li>
                </ul>

                <h3>Supported Regions</h3>
                <p>ResourceLens supports all AWS regions where AWS Config is available. 
                Check the <Link external href="https://docs.aws.amazon.com/config/latest/developerguide/supported-regions.html">
                AWS Config supported regions</Link> for the complete list.</p>

                <h3>What Will Be Created</h3>
                <ul>
                    <li><strong>Global Resources (once per account):</strong>
                        <ul>
                            <li>WorkloadDiscoveryRole - IAM role for ResourceLens to discover resources</li>
                            <li>WorkloadDiscoveryMyApplicationsRole - IAM role for MyApplications functionality</li>
                        </ul>
                    </li>
                    <li><strong>Regional Resources (per region):</strong>
                        <ul>
                            <li>AggregationAuthorization - Allows ResourceLens to aggregate Config data for each region</li>
                            <li>AWS Config resources (only if not using a NAWS Account)</li>
                        </ul>
                    </li>
                </ul>
            </TextContent>
        </SpaceBetween>
    </Container>
);

// CDK Constructs Section Component
const CDKConstructsSection = ({ cdkInstallationCode, cdkSingleRegionCode, cdkMultiRegionCode, cdkDeploymentCode }) => (
    <Container
        header={
            <Header
                variant="h2"
                description="Recommended approach with zero configuration required"
            >
                Option 1: CDK Constructs (Recommended)
            </Header>
        }
    >
        <SpaceBetween size="m">
            <Alert type="success" header="Why Choose CDK Constructs?">
                <ul>
                    <li><strong>No Manual work</strong> - Deploys IAM resources using CDK Construct</li>
                    <li><strong>Zero configuration</strong> - Uses production defaults automatically</li>
                    <li><strong>Easier maintenance</strong> - Updates handled through package versioning</li>
                </ul>
            </Alert>

            <Box>
                <Header variant="h3">Step 1: Installation</Header>
                <CodeView content={cdkInstallationCode} />
            </Box>

            <Box>
                <Header variant="h3">Step 2: Single Region Deployment</Header>
                <TextContent>
                    <p>For deploying ResourceLens to a single region (simplest approach):</p>
                </TextContent>
                <CodeView content={cdkSingleRegionCode} />
            </Box>

            <Box>
                <Header variant="h3">Step 3: Multi-Region Deployment (Optional)</Header>
                <TextContent>
                    <p>For deploying ResourceLens across multiple regions:</p>
                </TextContent>
                <CodeView content={cdkMultiRegionCode} />
            </Box>

            <Box>
                <Header variant="h3">Step 4: Deploy Your Stack</Header>
                <CodeView content={cdkDeploymentCode} />
            </Box>

            <Box>
                <Header variant="h3">Package Information</Header>
                <TextContent>
                    <ul>
                        <li><strong>Package URL:</strong> <Link external href="https://code.amazon.com/packages/ResourceLensCDKConstructs/trees/mainline">
                            https://code.amazon.com/packages/ResourceLensCDKConstructs/trees/mainline
                        </Link></li>
                        <li><strong>Brazil Package:</strong> ResourceLensCDKConstructs</li>
                        <li><strong>npm Package:</strong> @amzn/resource-lens-constructs</li>
                    </ul>
                </TextContent>
            </Box>
        </SpaceBetween>
    </Container>
);

// CloudFormation Section Component
const CloudFormationSection = ({ history }) => (
    <Container
        header={
            <Header
                variant="h2"
                description="Alternative method for users who cannot use CDK"
            >
                Option 2: CloudFormation Templates
            </Header>
        }
    >
        <SpaceBetween size="m">
            <Alert type="info" header="When to Use CloudFormation Templates">
                <ul>
                    <li>Your target account does not need CAZ access</li>
                    <li>You want to immediately onboard to ResourceLens without waiting for pipeline deployment</li>
                    <li>You prefer direct CloudFormation template deployment</li>
                </ul>
            </Alert>

            <TextContent>
                <h3>Step 1: Download Templates</h3>
                <p>Navigate to the <Link href={IMPORT} onFollow={(e) => { e.preventDefault(); history.push(IMPORT); }}>
                Import page</Link> to download the CloudFormation templates:</p>
                <ul>
                    <li><strong>Global Resources Template</strong> - Deploy once per account</li>
                    <li><strong>Regional Resources Template</strong> - Deploy to each target region</li>
                </ul>

                <h3>Step 2: Deploy Global Resources</h3>
                <ol>
                    <li>Open the AWS CloudFormation console in your target account</li>
                    <li>Create a new stack using the Global Resources template</li>
                    <li>Use default parameters (recommended) or customize as needed</li>
                    <li>Deploy the stack</li>
                </ol>

                <h3>Step 3: Deploy Regional Resources</h3>
                <ol>
                    <li>For each target region, create a new CloudFormation stack</li>
                    <li>Use the Regional Resources template</li>
                    <li>Deploy the stack in each region where you want ResourceLens discovery</li>
                </ol>

                <h3>Optional: Multi-Account/Region Deployment</h3>
                <p>For deploying across multiple accounts and regions, we recommend using:</p>
                <ul>
                    <li><Link external href="https://docs.aws.amazon.com/AWSCloudFormation/latest/UserGuide/stacksets-concepts.html">
                        AWS CloudFormation StackSets
                    </Link> - For centralized deployment</li>
                </ul>
            </TextContent>
        </SpaceBetween>
    </Container>
);

// Verification Section Component
const VerificationSection = ({ history }) => (
    <Container
        header={
            <Header variant="h2">
                Verification & Next Steps
            </Header>
        }
    >
        <SpaceBetween size="m">
            <TextContent>
                <h3>Verify Successful Deployment</h3>
                <p>After deploying ResourceLens resources, verify the deployment was successful:</p>
                
                <h4>Check IAM Roles</h4>
                <ul>
                    <li>Navigate to IAM → Roles in the AWS Console</li>
                    <li>Confirm these roles exist:
                        <ul>
                            <li><code>WorkloadDiscoveryRole-783495883715</code></li>
                            <li><code>WorkloadDiscoveryMyApplicationsRole-783495883715-{'region'}</code></li>
                        </ul>
                    </li>
                </ul>

                <h4>Check AWS Config</h4>
                <ul>
                    <li>Navigate to AWS Config in each target region</li>
                    <li>Confirm Config is enabled and recording</li>
                    <li>Verify AggregationAuthorization is configured</li>
                </ul>

                <h3>Import Accounts for Discovery</h3>
                <p>Once deployment is verified, you can import your accounts:</p>
                <ol>
                    <li>Navigate to the <Link href={IMPORT} onFollow={(e) => { e.preventDefault(); history.push(IMPORT); }}>
                        Import page</Link></li>
                    <li>Select the accounts and regions you deployed ResourceLens to</li>
                    <li>Confirm the deployment checkboxes</li>
                    <li>Click Import to begin discovery</li>
                </ol>

                <h3>Troubleshooting</h3>
                <p>If you encounter issues:</p>
                <ul>
                    <li><strong>Permission errors:</strong> Verify your AWS credentials have sufficient permissions</li>
                    <li><strong>Role creation failures:</strong> Check for existing roles with similar names</li>
                    <li><strong>Config issues:</strong> Ensure AWS Config is supported in your target regions</li>
                    <li><strong>Import failures:</strong> Verify roles were created successfully before importing</li>
                </ul>
            </TextContent>

            <Alert type="success" header="Ready to Import!">
                <Box>
                    Once you&apos;ve successfully deployed ResourceLens resources, you&apos;re ready to import your accounts for discovery.
                    <Box float="right" margin={{ top: 's' }}>
                        <Button
                            variant="primary"
                            onClick={() => history.push(IMPORT)}
                        >
                            Start Import Process
                        </Button>
                    </Box>
                </Box>
            </Alert>
        </SpaceBetween>
    </Container>
);

export default OnboardingGuidePage;
